import * as Sentry from '@sentry/react';

import { version } from '../../../package.json';
import { buildExifTiff, insertExif, type ExifEntry, type ExifIfds } from './jpegExif';

/**
 * Geoetiqueta de las fotos de la cámara de la app (spec 2026-09-23, fotos
 * geolocalizadas). El codificador genérico está en `jpegExif.ts`; aquí vive
 * QUÉ se escribe y CUÁNDO una lectura del GPS merece ir en la foto.
 */

/** D4: una lectura más vieja que esto ya no dice dónde se hizo la foto. */
export const MAX_FIX_AGE_MS = 120_000;

/**
 * D4: error horizontal máximo que se acepta, en metros. Deja fuera el permiso
 * «aproximado» de Android (~3 km²) y las lecturas viejas tomadas en marcha.
 */
export const MAX_ESTIMATED_ERROR_M = 200;

/** `Software` del EXIF: la versión exacta de la app que hizo la foto. */
export const EXIF_SOFTWARE = `ROIS ${version}`;

/** Lectura del GPS ya aceptada para una foto. */
export interface CaptureFix {
  latitude: number;
  longitude: number;
  /** Metros sobre el elipsoide WGS-84 (lo que da la Geolocation API), o null. */
  altitude: number | null;
  /** Error horizontal ESTIMADO: `accuracy` + lo que se ha podido mover desde el fix. */
  errorMeters: number;
  /** Momento del fix (epoch ms). */
  timestamp: number;
}

/**
 * ¿Vale esta lectura para la foto que se dispara en `now`?
 *
 * La precisión de una lectura se degrada con su edad si el operario se mueve:
 * a 80 km/h, 10 s son 220 m. Por eso el error que cuenta no es el `accuracy`
 * crudo sino `accuracy + velocidad × edad`, y ese mismo es el que se declara
 * luego en `GPSHPositioningError`. Sin velocidad conocida se asume parado.
 *
 * Devuelve `null` cuando es mejor no escribir GPS: ausencia es honesta, una
 * coordenada mala no.
 */
export function usableFix(
  position: GeolocationPosition | null | undefined,
  now: number,
): CaptureFix | null {
  if (!position) return null;
  const { latitude, longitude, accuracy, altitude, speed } = position.coords;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;

  // Un reloj adelantado no puede dar una edad negativa.
  const ageMs = Math.max(0, now - position.timestamp);
  if (ageMs > MAX_FIX_AGE_MS) return null;

  const drift =
    speed != null && Number.isFinite(speed) && speed > 0 ? speed * (ageMs / 1000) : 0;
  const errorMeters = (Number.isFinite(accuracy) ? accuracy : Infinity) + drift;
  if (!(errorMeters <= MAX_ESTIMATED_ERROR_M)) return null;

  return {
    latitude,
    longitude,
    altitude: altitude != null && Number.isFinite(altitude) ? altitude : null,
    errorMeters,
    timestamp: position.timestamp,
  };
}

/** Diezmilésimas de segundo de arco: ~3 mm, muy por debajo de cualquier GPS. */
const SECONDS_DENOMINATOR = 10_000;

/**
 * Grados decimales → [grados, minutos, segundos] como racionales sin signo
 * (el hemisferio va en el `*Ref`).
 *
 * Se redondea UNA vez, sobre el total en diezmilésimas de segundo, y luego se
 * reparte con divisiones enteras: así el acarreo sale solo. Redondear los
 * segundos al final podía producir `60″` (piexifjs tiene ese fallo).
 */
export function toDms(decimal: number): Array<[number, number]> {
  const unitsPerDegree = 3600 * SECONDS_DENOMINATOR;
  const total = Math.round(Math.abs(decimal) * unitsPerDegree);
  const degrees = Math.floor(total / unitsPerDegree);
  const rest = total - degrees * unitsPerDegree;
  const minutes = Math.floor(rest / (60 * SECONDS_DENOMINATOR));
  const seconds = rest - minutes * 60 * SECONDS_DENOMINATOR;
  return [
    [degrees, 1],
    [minutes, 1],
    [seconds, SECONDS_DENOMINATOR],
  ];
}

const pad = (n: number, width = 2) => String(n).padStart(width, '0');

/** «AAAA:MM:DD HH:MM:SS» en hora LOCAL del dispositivo (formato EXIF). */
const exifLocalDateTime = (d: Date) =>
  `${d.getFullYear()}:${pad(d.getMonth() + 1)}:${pad(d.getDate())} ` +
  `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

/** «+02:00». `getTimezoneOffset()` es POSITIVO al oeste de UTC: va invertido. */
const exifOffset = (d: Date) => {
  const minutes = -d.getTimezoneOffset();
  const abs = Math.abs(minutes);
  return `${minutes >= 0 ? '+' : '-'}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
};

const asciiCodes = (s: string) => Array.from(s, (c) => c.charCodeAt(0));

function gpsEntries(fix: CaptureFix): ExifEntry[] {
  const at = new Date(fix.timestamp);
  const entries: ExifEntry[] = [
    { tag: 0x0000, type: 1, value: [2, 3, 0, 0] }, // GPSVersionID
    { tag: 0x0001, type: 2, value: fix.latitude >= 0 ? 'N' : 'S' },
    { tag: 0x0002, type: 5, value: toDms(fix.latitude) },
    { tag: 0x0003, type: 2, value: fix.longitude >= 0 ? 'E' : 'W' },
    { tag: 0x0004, type: 5, value: toDms(fix.longitude) },
    // GPSTimeStamp: hora del FIX en UTC, con milisegundos.
    {
      tag: 0x0007,
      type: 5,
      value: [
        [at.getUTCHours(), 1],
        [at.getUTCMinutes(), 1],
        [at.getUTCSeconds() * 1000 + at.getUTCMilliseconds(), 1000],
      ],
    },
    { tag: 0x0012, type: 2, value: 'WGS-84' }, // GPSMapDatum
    {
      tag: 0x001d, // GPSDateStamp
      type: 2,
      value: `${at.getUTCFullYear()}:${pad(at.getUTCMonth() + 1)}:${pad(at.getUTCDate())}`,
    },
    // GPSHPositioningError, al centímetro y hacia ARRIBA: nunca se declara
    // menos error del que hay.
    { tag: 0x001f, type: 5, value: [[Math.ceil(fix.errorMeters * 100), 100]] },
  ];
  if (fix.altitude != null) {
    entries.push(
      { tag: 0x0005, type: 1, value: [fix.altitude < 0 ? 1 : 0] }, // GPSAltitudeRef
      { tag: 0x0006, type: 5, value: [[Math.round(Math.abs(fix.altitude) * 100), 100]] },
    );
  }
  return entries;
}

/**
 * Los tags de una foto de la cámara (D12). Puro.
 *
 * Lo que NO se escribe, a propósito: `Orientation` (el canvas ya sale derecho,
 * y si `preserveExif` la encuentra mal tipada RECHAZA la compresión entera),
 * dimensiones y miniatura (quedarían obsoletas tras reescalar), el `heading`
 * del GPS (es el rumbo de la marcha, no hacia dónde apunta la cámara) y
 * cualquier dato del operario o del dispositivo.
 */
export function captureExifTags({
  capturedAt,
  fix,
  software,
  uniqueId,
}: {
  capturedAt: Date;
  fix: CaptureFix | null;
  software: string;
  /** 32 hexadecimales. Exif 3.0: se asigna al capturar y no cambia nunca. */
  uniqueId: string;
}): ExifIfds {
  const local = exifLocalDateTime(capturedAt);
  const offset = exifOffset(capturedAt);
  return {
    ifd0: [
      { tag: 0x0131, type: 2, value: software }, // Software
      { tag: 0x0132, type: 2, value: local }, // DateTime
      { tag: 0x0213, type: 3, value: [1] }, // YCbCrPositioning: centrado
    ],
    exif: [
      { tag: 0x9000, type: 7, value: asciiCodes('0232') }, // ExifVersion
      { tag: 0x9003, type: 2, value: local }, // DateTimeOriginal
      { tag: 0x9010, type: 2, value: offset }, // OffsetTime
      { tag: 0x9011, type: 2, value: offset }, // OffsetTimeOriginal
      { tag: 0x9101, type: 7, value: [1, 2, 3, 0] }, // ComponentsConfiguration: YCbCr
      { tag: 0xa001, type: 3, value: [1] }, // ColorSpace: sRGB
      { tag: 0xa420, type: 2, value: uniqueId }, // ImageUniqueID
    ],
    gps: fix ? gpsEntries(fix) : [],
  };
}

/** 128 bits en 32 hexadecimales, que es la forma que pide `ImageUniqueID`. */
function newUniqueId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function readBytes(blob: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Escribe el EXIF de captura en una foto de la cámara, ya comprimida (D1).
 *
 * 🔴 Nunca se pierde una foto por los metadatos: si algo falla, se devuelve el
 * `File` original tal cual y se avisa a Sentry. El `File` nuevo conserva
 * nombre (alimenta el ROID), tipo (Content-Type del PUT) y `lastModified`.
 */
export async function stampCaptureExif(
  file: File,
  ctx: { capturedAt: Date; fix: CaptureFix | null },
): Promise<File> {
  if (file.type !== 'image/jpeg') return file;
  try {
    const jpeg = await readBytes(file);
    const tiff = buildExifTiff(
      captureExifTags({ ...ctx, software: EXIF_SOFTWARE, uniqueId: newUniqueId() }),
    );
    return new File([insertExif(jpeg, tiff)], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });
  } catch (err) {
    Sentry.captureException(err, { tags: { feature: 'camera', phase: 'geotag' } });
    return file;
  }
}
